import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/features/utils/i18n";
import type { EmailAccountForm, MailServerForm } from "./types";

/**
 * Providers that file a copy of every message sent over their SMTP themselves
 * (mirrors the backend's `providerFilesSentCopy`): copying there shows every
 * answer twice, so the box starts unticked.
 */
const FILES_ITS_OWN_COPY =
  /(^|\.)(gmail\.com|googlemail\.com|office365\.com|outlook\.com|hotmail\.com|live\.com)$/i;

interface EmailAccountFieldsProps {
  mode: "create" | "edit";
  value: EmailAccountForm;
  onChange: (changes: Partial<EmailAccountForm>) => void;
}

interface ServerFieldsProps {
  kind: "smtp" | "imap";
  mode: "create" | "edit";
  value: MailServerForm;
  address: string;
  onChange: (changes: Partial<MailServerForm>) => void;
}

/** SMTP or IMAP: the server, its port, TLS and the login. Same four fields. */
const ServerFields = ({
  kind,
  mode,
  value,
  address,
  onChange,
}: ServerFieldsProps) => {
  const { t } = useTranslation("connectors");
  const isSmtp = kind === "smtp";
  // The usual port when the field is left empty (backend DEFAULT_*_PORT).
  const defaultPort = isSmtp
    ? value.secure
      ? "465"
      : "587"
    : value.secure
      ? "993"
      : "143";

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <p className="text-sm font-medium">
        {isSmtp
          ? t("connectors.email.smtpTitle", "Envoi (SMTP)")
          : t("connectors.email.imapTitle", "Réception (IMAP)")}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7rem]">
        <div className="space-y-2">
          <Label htmlFor={`${kind}-host`}>
            {t("connectors.email.host", "Serveur")}{" "}
            <span className="text-red-500">*</span>
          </Label>
          <Input
            id={`${kind}-host`}
            value={value.host}
            onChange={(e) => onChange({ host: e.target.value })}
            placeholder={isSmtp ? "smtp.exemple.fr" : "imap.exemple.fr"}
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${kind}-port`}>
            {t("connectors.email.port", "Port")}
          </Label>
          <Input
            id={`${kind}-port`}
            inputMode="numeric"
            value={value.port}
            onChange={(e) => onChange({ port: e.target.value })}
            placeholder={defaultPort}
          />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id={`${kind}-secure`}
          checked={value.secure}
          onCheckedChange={(checked) => onChange({ secure: checked === true })}
        />
        <Label
          htmlFor={`${kind}-secure`}
          className="text-sm font-normal cursor-pointer"
        >
          {t(
            "connectors.email.secure",
            "Connexion chiffrée dès l'ouverture (SSL/TLS). Décoché : STARTTLS, toujours chiffré.",
          )}
        </Label>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${kind}-username`}>
            {t("connectors.email.username", "Identifiant")}
          </Label>
          <Input
            id={`${kind}-username`}
            value={value.username}
            onChange={(e) => onChange({ username: e.target.value })}
            placeholder={
              address ||
              t("connectors.email.usernamePlaceholder", "Adresse e-mail")
            }
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${kind}-password`}>
            {t("connectors.email.password", "Mot de passe")}
            {mode === "create" && <span className="text-red-500"> *</span>}
          </Label>
          <Input
            id={`${kind}-password`}
            type="password"
            value={value.password}
            onChange={(e) => onChange({ password: e.target.value })}
            placeholder={
              mode === "edit"
                ? t(
                    "connectors.email.passwordKeep",
                    "Laisser vide pour conserver le mot de passe actuel",
                  )
                : ""
            }
            autoComplete="new-password"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * The client's own mailbox: HeyUp reads it over IMAP and answers over SMTP.
 * The format rules and the "servers must be on the public internet" check are
 * the server's (`connectors/email/config.ts`); its message names the field.
 */
export const EmailAccountFields = ({
  mode,
  value,
  onChange,
}: EmailAccountFieldsProps) => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {t(
          "connectors.email.intro",
          "HeyUp lit cette boîte mail et répond aux messages reçus à la place de votre équipe. Seuls les messages arrivés après l'enregistrement sont traités ; les e-mails automatiques et les newsletters sont ignorés. Avec Gmail ou Outlook, utilisez un mot de passe d'application.",
        )}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="email-address">
            {t("connectors.email.address", "Adresse e-mail")}{" "}
            <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email-address"
            type="email"
            value={value.address}
            onChange={(e) => onChange({ address: e.target.value })}
            placeholder="contact@exemple.fr"
            autoComplete="off"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email-display-name">
            {t("connectors.email.displayName", "Nom affiché (facultatif)")}
          </Label>
          <Input
            id="email-display-name"
            value={value.displayName}
            onChange={(e) => onChange({ displayName: e.target.value })}
            placeholder="Salon Marie"
            autoComplete="off"
          />
        </div>
      </div>

      <ServerFields
        kind="smtp"
        mode={mode}
        value={value.smtp}
        address={value.address.trim()}
        onChange={(changes) =>
          onChange({ smtp: { ...value.smtp, ...changes } })
        }
      />
      <ServerFields
        kind="imap"
        mode={mode}
        value={value.imap}
        address={value.address.trim()}
        onChange={(changes) =>
          onChange({ imap: { ...value.imap, ...changes } })
        }
      />

      <SentCopyFields value={value} onChange={onChange} />

      <div className="space-y-2">
        <Label htmlFor="email-mailbox">
          {t("connectors.email.mailbox", "Dossier à surveiller (facultatif)")}
        </Label>
        <Input
          id="email-mailbox"
          value={value.mailbox}
          onChange={(e) => onChange({ mailbox: e.target.value })}
          placeholder="INBOX"
          autoComplete="off"
        />
      </div>
    </div>
  );
};

/** Whether answers are copied into the mailbox's Sent folder, and which one. */
const SentCopyFields = ({
  value,
  onChange,
}: {
  value: EmailAccountForm;
  onChange: (changes: Partial<EmailAccountForm>) => void;
}) => {
  const { t } = useTranslation("connectors");
  const providerCopies = FILES_ITS_OWN_COPY.test(value.smtp.host.trim());
  const checked = value.saveToSent ?? !providerCopies;

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="email-save-sent"
          checked={checked}
          onCheckedChange={(next) => onChange({ saveToSent: next === true })}
        />
        <Label
          htmlFor="email-save-sent"
          className="text-sm font-normal cursor-pointer"
        >
          {t(
            "connectors.email.saveToSent",
            "Copier les réponses dans le dossier Envoyés",
          )}
        </Label>
      </div>
      <p className="text-xs text-muted-foreground">
        {providerCopies
          ? t(
              "connectors.email.saveToSentProvider",
              "Gmail et Microsoft 365 enregistrent déjà eux-mêmes les messages envoyés : cocher cette case les ferait apparaître en double.",
            )
          : t(
              "connectors.email.saveToSentHint",
              "Votre équipe voit ainsi, dans sa messagerie habituelle, ce que l'agent a écrit en son nom.",
            )}
      </p>
      {checked && (
        <div className="space-y-2">
          <Label htmlFor="email-sent-folder">
            {t("connectors.email.sentFolder", "Dossier Envoyés (facultatif)")}
          </Label>
          <Input
            id="email-sent-folder"
            value={value.sentFolder}
            onChange={(e) => onChange({ sentFolder: e.target.value })}
            placeholder={t(
              "connectors.email.sentFolderPlaceholder",
              "Détecté automatiquement",
            )}
            autoComplete="off"
          />
        </div>
      )}
    </div>
  );
};
